import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import type { NumericMenuConnectorParamsItem } from 'instantsearch.js/es/connectors/numeric-menu/connectNumericMenu';

import type { IAlgoliaRefinement } from '../types';
import { AlgoliaIndice } from '../types';

type SortKey = 'count:asc' | 'count:desc' | 'name:asc' | 'name:desc' | 'isRefined';

export const SEARCH_PAGE_PATH = 'search';
export const FEATURED_BRANDS_PATH = 'featured-brands';

export const FILTERS = {
    DRAWER: {
        LABEL: 'Drawer Product Filters',
        DESCRIPTION: 'Drawer Product Filters Description',
    },
    HEADER_TITLE: {
        MOBILE: 'Sort & filter',
        DESKTOP: 'All filters',
    },
    REFINEMENT_LIST: {
        SHOW_MORE: false,
        SHOW_MORE_LIMIT: 2000,
        LIMIT: 500,
        SEARCHABLE: true,
        SEARCH_PLACEHOLDER: 'Search for ...',
        SHOW_ACTIONS: true,
    },
    SHOP_BY_STORE: {
        TITLE: 'Shop by store',
        CHOOSE_STORE: 'Choose Store',
        CHANGE_STORE: 'Change store',
        NEAR_ME: 'Find a store near me',
        CLEAR: 'Clear',
        VIEW_RESULTS: 'View results',
        ATTRIBUTE: 'inventory',
        LABEL: 'Pick up in store',
    },
    BOPIS_ELIGIBLE: {
        DEFAULT_VALUE: 'true',
        ATTRIBUTE: 'shoppingOptions-isBopisEligible',
    },
    BRAND: {
        ATTRIBUTE: 'brand',
        SHOW_MORE_LIMIT: 2000,
        LIMIT: 6,
        TITLE: 'Brand',
        PLACEHOLDER: 'Search brands',
    },
    RANGE: {
        ATTRIBUTE: 'price.number',
        SHOW_RESET: true,
        MIN_VALUE: 0,
        MAX_VALUE: 50,
        MIN: {
            MIN_VALUE: 0,
            MAX_VALUE: 50,
        },
        MAX: {
            MIN_VALUE: 1,
            MAX_VALUE: 50,
        },
        MIN_GAP: 1,
        TITLE: 'Price',
    },
    RATING: {
        ATTRIBUTE: 'customBvAverageRating',
        SHOW_ACTIONS: true,
        ORDER: 'name:desc' as SortKey,
        MAX_STARS: 5,
        TITLE: 'Rating',
        STAR: 'star',
        STARS: 'stars',
        UP: ' & up',
        ITEMS: [
            { label: '5', start: 5 },
            { label: '4', start: 4 },
            { label: '3', start: 3 },
            { label: '2', start: 2 },
            { label: '1', start: 1 },
        ] as NumericMenuConnectorParamsItem[],
    },
    FLAVOR: {
        ATTRIBUTE: 'flavorList',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Flavor',
    },
    HEALTH: {
        ATTRIBUTE: 'customHealthConsideration',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Health Consideration',
    },
    BREED_SIZE: {
        ATTRIBUTE: 'kibbleSizes',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Breed size',
    },
    LIFESTAGE: {
        ATTRIBUTE: 'dogLifestages',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Dog Lifestage',
    },
    CUSTOM_PET: {
        ATTRIBUTE: 'customPet',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Pet',
    },
    CUSTOM_CATEGORY: {
        ATTRIBUTE: 'customCategoryList',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Category',
    },
    /** Legacy facet attribute; keep facetable alongside {@link FILTERS.CUSTOM_CATEGORY} for mobile and deep links. */
    CUSTOM_CATEGORY_LEGACY: {
        ATTRIBUTE: 'customCategory',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Category',
    },
    CUSTOM_CATEGORY_NAMES: {
        ATTRIBUTE: 'custom_category_names',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'Custom Category',
    },
    CUSTOM_WAYS_SHOP: {
        ATTRIBUTE: 'moreWaysToShop',
        LIMIT: 8,
        SHOW_MORE_LIMIT: 2000,
        TITLE: 'More Ways to Shop',
    },
    TANK_SIZE: {
        ATTRIBUTE: 'tank-sizes',
        TITLE: 'Tank Size',
    },
    GENDER: {
        ATTRIBUTE: 'genders',
        TITLE: 'Gender',
    },
    SPORTS_LEAGUE: {
        ATTRIBUTE: 'sports-league',
        TITLE: 'League',
    },
    PRODUCT_LIFETIME: {
        ATTRIBUTE: 'productLifetime',
        TITLE: 'Duration',
    },
    MATERIALS: {
        ATTRIBUTE: 'materials',
        TITLE: 'Material',
    },
    PRODUCT_APPLICATION_METHODS: {
        ATTRIBUTE: 'productApplicationMethods',
        TITLE: 'Application',
    },
    TWO_DIMENSION_SIZE: {
        ATTRIBUTE: 'size.twoDimensionalSize',
        TITLE: 'Size',
    },
    PET_TYPE_AMPHIBIANS: {
        ATTRIBUTE: 'petType-amphibians',
        TITLE: 'Amphibians',
    },
    BED_FEATURES: {
        ATTRIBUTE: 'bed-features',
        TITLE: 'Bed Features',
    },
    BED_SIZE: {
        ATTRIBUTE: 'bed-sizeDisplays',
        TITLE: 'Bed Size',
    },
    BED_TYPES: {
        ATTRIBUTE: 'bed-types',
        TITLE: 'Bed Type',
    },
    THREE_DIMENSION_SIZE: {
        ATTRIBUTE: 'size.threeDimensionalSize',
        TITLE: 'Size',
    },
    BIRD_CAGE_DISPLAYS: {
        ATTRIBUTE: 'birdCageDisplays',
        TITLE: 'Size',
    },
    BIRD_FOOD_TYPES: {
        ATTRIBUTE: 'birdFoodTypes',
        TITLE: 'Bird Food Type',
    },
    PET_TYPE_BIRDS: {
        ATTRIBUTE: 'petType-birds',
        TITLE: 'Birds',
    },
    PET_TYPE_BREED: {
        ATTRIBUTE: 'petType-breeds',
        TITLE: 'Breed',
    },
    BULB_TYPE: {
        ATTRIBUTE: 'bulbType',
        TITLE: 'Bulb Type',
    },
    CAT_LIFE_STAGES: {
        ATTRIBUTE: 'catLifestages',
        TITLE: 'Cat Lifestage',
    },
    CAT_WEIGHT_RANGE: {
        ATTRIBUTE: 'catWeightRange',
        TITLE: 'Weight Range',
    },
    CAT_WEIGHT_RANGE_DISPLAY: {
        ATTRIBUTE: 'catWeightRangeDisplays',
        TITLE: 'Weight',
    },
    COAT_TYPES: {
        ATTRIBUTE: 'coatTypes',
        TITLE: 'Coat Type',
    },
    COLLAR_LEASH_SIZE_DISPLAY: {
        ATTRIBUTE: 'collarAndLeashSizeDisplays',
        TITLE: 'Size',
    },
    COLLECTION: {
        ATTRIBUTE: 'collection',
        TITLE: 'Collection',
    },
    COLOR_VALUE: {
        ATTRIBUTE: 'color-value',
        TITLE: 'Color',
    },
    COLOR_SWATCH_DISPLAY: {
        ATTRIBUTE: 'color-swatchDisplay',
        TITLE: 'Color',
    },
    COLOR_FAMILIES: {
        ATTRIBUTE: 'color-families',
        TITLE: 'Color',
    },
    CLIMATE_CONTROL_DEVICES: {
        ATTRIBUTE: 'climateControlDevices',
        TITLE: 'Control Type',
    },
    COSTUME_TYPES: {
        ATTRIBUTE: 'costumeTypes',
        TITLE: 'Costume Type',
    },
    CRATE_CARRIER_SIZE: {
        ATTRIBUTE: 'crateAndCarrier-sizeDisplays',
        TITLE: 'Crate & Carrier Size',
    },
    CRATE_CARRIER_TYPE: {
        ATTRIBUTE: 'crateAndCarrier-types',
        TITLE: 'Crate & Carrier Type',
    },
    CRATE_MATS_PADS_DISPLAY: {
        ATTRIBUTE: 'crateMatsAndPadsSizeDisplays',
        TITLE: 'Mats and Pads Size',
    },
    DIMENSION_RANGES: {
        ATTRIBUTE: 'dimensionRanges',
        TITLE: 'Dimension Range',
    },
    DISCONTINUED_OVERRIDE: {
        ATTRIBUTE: 'isDiscontinuedOverride',
        TITLE: 'Discontinued Override',
    },
    DOG_ACCESSORIES: {
        ATTRIBUTE: 'dogAccessories',
        TITLE: 'Accessories',
    },
    DOG_WEIGHT_RANGE: {
        ATTRIBUTE: 'dogWeightRangeDisplays',
        TITLE: 'Weight',
    },
    DOOR_GATE_TYPE: {
        ATTRIBUTE: 'doorAndGateTypes',
        TITLE: 'Door and Gate Type',
    },
    ENVIRONMENT: {
        ATTRIBUTE: 'environments',
        TITLE: 'Environment',
    },
    EXPERIENCE_LEVEL: {
        ATTRIBUTE: 'experienceLevels',
        TITLE: 'Experience Level',
    },
    FENCE_SYSTEM_TYPE: {
        ATTRIBUTE: 'fenceSystemTypes',
        TITLE: 'Fence System Type',
    },
    FILTER_TYPE: {
        ATTRIBUTE: 'filterType',
        TITLE: 'Filter Type',
    },
    FISH_FEEDER_TYPE: {
        ATTRIBUTE: 'fishFeederTypes',
        TITLE: 'Feeder Type',
    },
    FISH_FOOD_TYPES: {
        ATTRIBUTE: 'fishFoodTypes',
        TITLE: 'Fish Food Type',
    },
    PET_TYPE_FISH: {
        ATTRIBUTE: 'petType-fishSpecies',
        TITLE: 'Fish Species',
    },
    FLAVOR_VALUE: {
        ATTRIBUTE: 'flavor.value',
        TITLE: 'Flavor',
    },
    SIZE_FLUID: {
        ATTRIBUTE: 'size.fluidSize',
        TITLE: 'Size',
    },
    FOOD_CATEGORY: {
        ATTRIBUTE: 'foodCategory',
        TITLE: 'Food Category',
    },
    FOOD_FORMS: {
        ATTRIBUTE: 'foodForms',
        TITLE: 'Food Form',
    },
    FOOD_OUNCES_SIZE: {
        ATTRIBUTE: 'foodOuncesSizeDisplays',
        TITLE: 'Ounces',
    },
    FOOD_POUNDS_SIZE: {
        ATTRIBUTE: 'foodPoundsSizeDisplays',
        TITLE: 'Pounds',
    },
    FOOD_STORAGE_CAPACITY: {
        ATTRIBUTE: 'foodStorageCapacity',
        TITLE: 'Food Storage Capacity',
    },
    FOOD_TEXTURE: {
        ATTRIBUTE: 'foodTextures',
        TITLE: 'Food Texture',
    },
    FURNITURE_HEIGHT: {
        ATTRIBUTE: 'furnitureHeight',
        TITLE: 'Furniture Height',
    },
    GATE_SIZE: {
        ATTRIBUTE: 'gateSizes',
        TITLE: 'Gate Special Size',
    },
    GROOMING_TYPES: {
        ATTRIBUTE: 'groomingTypes',
        TITLE: 'Grooming Type',
    },
    HABITAT_ACCESSORIES: {
        ATTRIBUTE: 'habitat-accessories',
        TITLE: 'Habitat Accessories',
    },
    HABITAT_DECOR_TYPE: {
        ATTRIBUTE: 'habitat-decorTypes',
        TITLE: 'Décor Type',
    },
    SUBSCRIPTION_ENABLED: {
        ATTRIBUTE: 'isSubscriptionEnabled',
        TITLE: 'Autoship',
        ELIGIBLE_LABEL: 'Autoship eligible',
    },
    LEASH_TYPE: {
        ATTRIBUTE: 'leashTypes',
        TITLE: 'Leash Type',
    },
    LITTER_BOX_TYPE: {
        ATTRIBUTE: 'litterBoxType',
        TITLE: 'Litter Box Type',
    },
    LITTER_FEATURES: {
        ATTRIBUTE: 'litter-features',
        TITLE: 'Litter Features',
    },
    LITTER_MATERIALS: {
        ATTRIBUTE: 'litter-materials',
        TITLE: 'Litter Material',
    },
    LITTER_TYPE: {
        ATTRIBUTE: 'litter-types',
        TITLE: 'Litter Type',
    },
    LIVE_PET: {
        ATTRIBUTE: 'isLivePet',
        TITLE: 'Live Pet',
    },
    MATURITY_SIZE: {
        ATTRIBUTE: 'maturitySizes',
        TITLE: 'Maturity Size',
    },
    NUTRITIONAL_OPTION: {
        ATTRIBUTE: 'nutritionalOptions',
        TITLE: 'Nutritional Option',
    },
    PACKAGE_TYPE: {
        ATTRIBUTE: 'packagingTypes',
        TITLE: 'Package Type',
    },
    PET_TYPE_PETS: {
        ATTRIBUTE: 'petType-pets',
        TITLE: 'Pet',
    },
    PET_SIZE_DISPLAY: {
        ATTRIBUTE: 'petSizeDisplays',
        TITLE: 'Size',
    },
    SIZE_PET_SIZE: {
        ATTRIBUTE: 'size.petSize',
        TITLE: 'Size',
    },
    PHARMACY_SKU: {
        ATTRIBUTE: 'pharmacy-sku',
        TITLE: 'Pharmacy Sku',
    },
    PHARMACY_TYPE: {
        ATTRIBUTE: 'pharmacy-type',
        TITLE: 'Pharmacy Type',
    },
    PHARMACY_COLD_STORAGE: {
        ATTRIBUTE: 'pharmacy.isColdStorage',
        TITLE: 'Pharmacy Cold Storage',
    },
    PHARMACY_OUT_OF_STOCK: {
        ATTRIBUTE: 'pharmacy.isOos',
        TITLE: 'Pharmacy Out of Stock',
    },
    QUANTITY: {
        ATTRIBUTE: 'quantity',
        TITLE: 'Quantity',
    },
    QUANTITY_DISPLAY: {
        ATTRIBUTE: 'quantityDisplays',
        TITLE: 'Quantity',
    },
    FENCE_RANGES: {
        ATTRIBUTE: 'fenceRanges',
        TITLE: 'Range',
    },
    REPTILE_FOOD_TYPE: {
        ATTRIBUTE: 'reptileFoodType',
        TITLE: 'Reptile Food Type',
    },
    PET_TYPE_REPTILES: {
        ATTRIBUTE: 'petType-reptiles',
        TITLE: 'Reptiles',
    },
    PHARMACY_STRENGTH: {
        ATTRIBUTE: 'pharmacy-rxStrength',
        TITLE: 'Pharmacy Strength',
    },
    SCENT: {
        ATTRIBUTE: 'scent',
        TITLE: 'Scent',
    },
    SCHEDULED_DELIVERY_ELIGIBLE: {
        ATTRIBUTE: 'customScheduledDeliveryEligible',
        TITLE: 'FREE Same-Day Delivery',
        ELIGIBLE_LABEL: 'FREE Same-Day Delivery eligible',
    },
    SERIES: {
        ATTRIBUTE: 'series',
        TITLE: 'Series',
    },
    SHAPES: {
        ATTRIBUTE: 'shapes',
        TITLE: 'Shapes',
    },
    PET_TYPE_SMALL_PETS: {
        ATTRIBUTE: 'petType-smallPets',
        TITLE: 'Small Pet',
    },
    SMALL_PET_ACCESSORIES: {
        ATTRIBUTE: 'smallPetAccessoryTypes',
        TITLE: 'Accessories',
    },
    SIZE_SOLID_SIZE: {
        ATTRIBUTE: 'size.solidSize',
        TITLE: 'Size',
    },
    SPORTS_TEAM: {
        ATTRIBUTE: 'sports-team',
        TITLE: 'Sports Team',
    },
    STAIN_TYPES: {
        ATTRIBUTE: 'stainTypes',
        TITLE: 'Stain Type',
    },
    STAND_TYPE: {
        ATTRIBUTE: 'standType',
        TITLE: 'Stand Type',
    },
    SHIP_TO_HOME_CASE: {
        ATTRIBUTE: 'shipToHome-isCaseRequired',
        TITLE: 'Ship To Home Case',
    },
    ITEMS_PER_PACK: {
        ATTRIBUTE: 'itemsPerPackOptions',
        TITLE: 'Supply',
    },
    DESIGN_THEME: {
        ATTRIBUTE: 'designThemes',
        TITLE: 'Theme',
    },
    TOY_FEATURES: {
        ATTRIBUTE: 'toyFeatures',
        TITLE: 'Toy Features',
    },
    TREAT_TYPE: {
        ATTRIBUTE: 'treatTypes',
        TITLE: 'Treat Type',
    },
    VET_AUTHORIZED_DIET: {
        ATTRIBUTE: 'vetAuthorizedDiets',
        TITLE: 'Vet Authorized Diets',
    },
    WATER_TREATMENT: {
        ATTRIBUTE: 'waterTreatment',
        TITLE: 'Water Treatment',
    },
    WATER_TYPES: {
        ATTRIBUTE: 'waterTypes',
        TITLE: 'Water Type',
    },
    WATTAGE: {
        ATTRIBUTE: 'wattage',
        TITLE: 'Wattage',
    },
    WATTAGE_RANGES: {
        ATTRIBUTE: 'wattageRanges',
        TITLE: 'Wattage',
    },
    AVERAGE_LIFESPANS: {
        ATTRIBUTE: 'averageLifespans',
        TITLE: 'Average Lifespan',
    },
    REVIEW_COUNT: {
        ATTRIBUTE: 'bvReviewCount',
        TITLE: 'Review Count',
    },
    INTENDED_PRODUCT_USE: {
        ATTRIBUTE: 'intendedProductUses',
        TITLE: 'Activity',
    },
    SORT_BY: {
        TITLE: 'Sort by',
    },
    CLEAR: {
        HEADER: 'Filters applied',
        TITLE: 'Clear',
        ALL: 'Clear all',
    },
    VIEW_RESULTS: {
        TITLE: 'View results',
    },
    RESET: {
        TITLE: 'Reset',
    },
    EVENT: {
        VALUE: 'product_list_filter',
    },
};

export const ORDERED_FILTERS: IAlgoliaRefinement[] = [
    {
        attribute: FILTERS.PRODUCT_APPLICATION_METHODS.ATTRIBUTE,
        urlName: 'methods',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE,
        urlName: 'bopisEligible',
        isNumeric: false,
    },
    {
        attribute: FILTERS.AVERAGE_LIFESPANS.ATTRIBUTE,
        urlName: 'lifespan',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BRAND.ATTRIBUTE,
        urlName: 'brand',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SHOP_BY_STORE.ATTRIBUTE,
        urlName: 'store',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CUSTOM_CATEGORY.ATTRIBUTE,
        urlName: 'category',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CUSTOM_CATEGORY_LEGACY.ATTRIBUTE,
        urlName: 'customCategory',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CUSTOM_PET.ATTRIBUTE,
        urlName: 'pet',
        isNumeric: false,
    },
    {
        attribute: FILTERS.RANGE.ATTRIBUTE,
        urlName: 'price',
        isNumeric: true,
    },
    {
        attribute: FILTERS.RATING.ATTRIBUTE,
        urlName: 'rating',
        isNumeric: true,
    },
    {
        attribute: FILTERS.FLAVOR.ATTRIBUTE,
        urlName: 'flavor',
        isNumeric: false,
    },
    {
        attribute: FILTERS.HEALTH.ATTRIBUTE,
        urlName: 'health',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BREED_SIZE.ATTRIBUTE,
        urlName: 'breedSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.LIFESTAGE.ATTRIBUTE,
        urlName: 'dogLifestage',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CUSTOM_WAYS_SHOP.ATTRIBUTE,
        urlName: 'shop',
        isNumeric: false,
    },
    {
        attribute: FILTERS.TANK_SIZE.ATTRIBUTE,
        urlName: 'tankSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PRODUCT_LIFETIME.ATTRIBUTE,
        urlName: 'productLifetime',
        isNumeric: false,
    },
    {
        attribute: FILTERS.GENDER.ATTRIBUTE,
        urlName: 'genders',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SPORTS_LEAGUE.ATTRIBUTE,
        urlName: 'sportLeague',
        isNumeric: false,
    },
    {
        attribute: FILTERS.MATERIALS.ATTRIBUTE,
        urlName: 'material',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BED_FEATURES.ATTRIBUTE,
        urlName: 'bedFeature',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BED_SIZE.ATTRIBUTE,
        urlName: 'bedSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BED_TYPES.ATTRIBUTE,
        urlName: 'bedType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BIRD_CAGE_DISPLAYS.ATTRIBUTE,
        urlName: 'birdCage',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BIRD_FOOD_TYPES.ATTRIBUTE,
        urlName: 'birdFood',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_TYPE_BIRDS.ATTRIBUTE,
        urlName: 'birdType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_TYPE_BREED.ATTRIBUTE,
        urlName: 'breedType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.BULB_TYPE.ATTRIBUTE,
        urlName: 'bulbType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CAT_LIFE_STAGES.ATTRIBUTE,
        urlName: 'catLifestage',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CAT_WEIGHT_RANGE.ATTRIBUTE,
        urlName: 'catWeightRange',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CAT_WEIGHT_RANGE_DISPLAY.ATTRIBUTE,
        urlName: 'catWeightRangeDisplay',
        isNumeric: false,
    },
    {
        attribute: FILTERS.COAT_TYPES.ATTRIBUTE,
        urlName: 'coatType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.COLLAR_LEASH_SIZE_DISPLAY.ATTRIBUTE,
        urlName: 'collarLeashSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.COLLECTION.ATTRIBUTE,
        urlName: 'collection',
        isNumeric: false,
    },
    {
        attribute: FILTERS.COLOR_VALUE.ATTRIBUTE,
        urlName: 'colorValue',
        isNumeric: false,
    },
    {
        attribute: FILTERS.COLOR_SWATCH_DISPLAY.ATTRIBUTE,
        urlName: 'colorSwatchDisplay',
        isNumeric: false,
    },
    {
        attribute: FILTERS.COLOR_FAMILIES.ATTRIBUTE,
        urlName: 'colorFamily',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CLIMATE_CONTROL_DEVICES.ATTRIBUTE,
        urlName: 'climateControl',
        isNumeric: false,
    },
    {
        attribute: FILTERS.COSTUME_TYPES.ATTRIBUTE,
        urlName: 'costumeType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CRATE_CARRIER_TYPE.ATTRIBUTE,
        urlName: 'crateCarrierType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CRATE_CARRIER_SIZE.ATTRIBUTE,
        urlName: 'crateSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.CRATE_MATS_PADS_DISPLAY.ATTRIBUTE,
        urlName: 'crateMatPad',
        isNumeric: false,
    },
    {
        attribute: FILTERS.DIMENSION_RANGES.ATTRIBUTE,
        urlName: 'dimensionRange',
        isNumeric: false,
    },
    {
        attribute: FILTERS.DISCONTINUED_OVERRIDE.ATTRIBUTE,
        urlName: 'discountedOverride',
        isNumeric: false,
    },
    {
        attribute: FILTERS.DOG_ACCESSORIES.ATTRIBUTE,
        urlName: 'dogAccessory',
        isNumeric: false,
    },
    {
        attribute: FILTERS.DOG_WEIGHT_RANGE.ATTRIBUTE,
        urlName: 'dogWeihtRange',
        isNumeric: false,
    },
    {
        attribute: FILTERS.DOOR_GATE_TYPE.ATTRIBUTE,
        urlName: 'doorGateType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.ENVIRONMENT.ATTRIBUTE,
        urlName: 'environment',
        isNumeric: false,
    },
    {
        attribute: FILTERS.EXPERIENCE_LEVEL.ATTRIBUTE,
        urlName: 'experienceLevel',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FENCE_SYSTEM_TYPE.ATTRIBUTE,
        urlName: 'fenceSystem',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FILTER_TYPE.ATTRIBUTE,
        urlName: 'filterType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FISH_FEEDER_TYPE.ATTRIBUTE,
        urlName: 'fishFeederType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FISH_FOOD_TYPES.ATTRIBUTE,
        urlName: 'fishFoodType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_TYPE_FISH.ATTRIBUTE,
        urlName: 'petFishType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FLAVOR_VALUE.ATTRIBUTE,
        urlName: 'flavorValue',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SIZE_FLUID.ATTRIBUTE,
        urlName: 'sizeFluid',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FOOD_CATEGORY.ATTRIBUTE,
        urlName: 'foodCategory',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FOOD_FORMS.ATTRIBUTE,
        urlName: 'foodForm',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FOOD_OUNCES_SIZE.ATTRIBUTE,
        urlName: 'foodOunce',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FOOD_POUNDS_SIZE.ATTRIBUTE,
        urlName: 'foodPounds',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FOOD_STORAGE_CAPACITY.ATTRIBUTE,
        urlName: 'foodStorageCapacity',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FOOD_TEXTURE.ATTRIBUTE,
        urlName: 'foodTexture',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FURNITURE_HEIGHT.ATTRIBUTE,
        urlName: 'furnitureHeight',
        isNumeric: false,
    },
    {
        attribute: FILTERS.GATE_SIZE.ATTRIBUTE,
        urlName: 'gateSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.GROOMING_TYPES.ATTRIBUTE,
        urlName: 'groomingType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.HABITAT_ACCESSORIES.ATTRIBUTE,
        urlName: 'habitatAccessory',
        isNumeric: false,
    },
    {
        attribute: FILTERS.HABITAT_DECOR_TYPE.ATTRIBUTE,
        urlName: 'decorType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE,
        urlName: 'subscriptionEnabled',
        isNumeric: false,
    },
    {
        attribute: FILTERS.LEASH_TYPE.ATTRIBUTE,
        urlName: 'leashType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.LITTER_BOX_TYPE.ATTRIBUTE,
        urlName: 'litterBoxType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.LITTER_FEATURES.ATTRIBUTE,
        urlName: 'litterFeature',
        isNumeric: false,
    },
    {
        attribute: FILTERS.LITTER_MATERIALS.ATTRIBUTE,
        urlName: 'litterMaterial',
        isNumeric: false,
    },
    {
        attribute: FILTERS.LITTER_TYPE.ATTRIBUTE,
        urlName: 'litterType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.LIVE_PET.ATTRIBUTE,
        urlName: 'livePet',
        isNumeric: false,
    },
    {
        attribute: FILTERS.MATURITY_SIZE.ATTRIBUTE,
        urlName: 'maturitySize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.NUTRITIONAL_OPTION.ATTRIBUTE,
        urlName: 'nutritionalOption',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PACKAGE_TYPE.ATTRIBUTE,
        urlName: 'packageType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_TYPE_PETS.ATTRIBUTE,
        urlName: 'petType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_SIZE_DISPLAY.ATTRIBUTE,
        urlName: 'petSizeDisplay',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SIZE_PET_SIZE.ATTRIBUTE,
        urlName: 'petSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PHARMACY_SKU.ATTRIBUTE,
        urlName: 'pharmacySku',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PHARMACY_TYPE.ATTRIBUTE,
        urlName: 'pharmacyType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PHARMACY_COLD_STORAGE.ATTRIBUTE,
        urlName: 'pharmacyColdStorage',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PHARMACY_OUT_OF_STOCK.ATTRIBUTE,
        urlName: 'pharmacyOutStock',
        isNumeric: false,
    },
    {
        attribute: FILTERS.QUANTITY.ATTRIBUTE,
        urlName: 'quantity',
        isNumeric: false,
    },
    {
        attribute: FILTERS.QUANTITY_DISPLAY.ATTRIBUTE,
        urlName: 'quantityDisplay',
        isNumeric: false,
    },
    {
        attribute: FILTERS.FENCE_RANGES.ATTRIBUTE,
        urlName: 'fenceRange',
        isNumeric: false,
    },
    {
        attribute: FILTERS.REPTILE_FOOD_TYPE.ATTRIBUTE,
        urlName: 'reptileFoodType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_TYPE_REPTILES.ATTRIBUTE,
        urlName: 'reptileType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_TYPE_AMPHIBIANS.ATTRIBUTE,
        urlName: 'amphibianType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PHARMACY_STRENGTH.ATTRIBUTE,
        urlName: 'pharmacyStrength',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SCENT.ATTRIBUTE,
        urlName: 'scent',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE,
        urlName: 'scheduledDelivery',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SERIES.ATTRIBUTE,
        urlName: 'serie',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SHAPES.ATTRIBUTE,
        urlName: 'shape',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SPORTS_TEAM.ATTRIBUTE,
        urlName: 'sports',
        isNumeric: false,
    },
    {
        attribute: FILTERS.PET_TYPE_SMALL_PETS.ATTRIBUTE,
        urlName: 'smallPetType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SMALL_PET_ACCESSORIES.ATTRIBUTE,
        urlName: 'smallPetAccessory',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SIZE_SOLID_SIZE.ATTRIBUTE,
        urlName: 'solidSize',
        isNumeric: false,
    },
    {
        attribute: FILTERS.STAIN_TYPES.ATTRIBUTE,
        urlName: 'stainType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.STAND_TYPE.ATTRIBUTE,
        urlName: 'standType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.SHIP_TO_HOME_CASE.ATTRIBUTE,
        urlName: 'shipToHome',
        isNumeric: false,
    },
    {
        attribute: FILTERS.ITEMS_PER_PACK.ATTRIBUTE,
        urlName: 'itemPerPackage',
        isNumeric: false,
    },
    {
        attribute: FILTERS.DESIGN_THEME.ATTRIBUTE,
        urlName: 'designTheme',
        isNumeric: false,
    },
    {
        attribute: FILTERS.TOY_FEATURES.ATTRIBUTE,
        urlName: 'toyFeature',
        isNumeric: false,
    },
    {
        attribute: FILTERS.TREAT_TYPE.ATTRIBUTE,
        urlName: 'treatType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.VET_AUTHORIZED_DIET.ATTRIBUTE,
        urlName: 'vetAuthorized',
        isNumeric: false,
    },
    {
        attribute: FILTERS.WATER_TREATMENT.ATTRIBUTE,
        urlName: 'waterTreatment',
        isNumeric: false,
    },
    {
        attribute: FILTERS.WATER_TYPES.ATTRIBUTE,
        urlName: 'waterType',
        isNumeric: false,
    },
    {
        attribute: FILTERS.WATTAGE.ATTRIBUTE,
        urlName: 'wattage',
        isNumeric: false,
    },
    {
        attribute: FILTERS.WATTAGE_RANGES.ATTRIBUTE,
        urlName: 'wattageRange',
        isNumeric: false,
    },
    {
        attribute: FILTERS.THREE_DIMENSION_SIZE.ATTRIBUTE,
        urlName: 'size3d',
        isNumeric: false,
    },
    {
        attribute: FILTERS.TWO_DIMENSION_SIZE.ATTRIBUTE,
        urlName: 'size2d',
        isNumeric: false,
    },
    {
        attribute: FILTERS.REVIEW_COUNT.ATTRIBUTE,
        urlName: 'reviewCount',
        isNumeric: false,
    },
    {
        attribute: FILTERS.INTENDED_PRODUCT_USE.ATTRIBUTE,
        urlName: 'intendedUse',
        isNumeric: false,
    },
];

export const DEFAULT_SEARCH_ORDER = 'Relevance';
export const DEFAULT_PLP_ORDER = 'Best sellers';

export const SORT_INDEX_SEARCH = {
    items: [
        {
            label: DEFAULT_SEARCH_ORDER,
            value: AlgoliaIndice.PRODUCTS,
            urlValue: 'relevance',
        },
        {
            label: 'Price: low to high',
            value: AlgoliaIndice.PRODUCTS_LOW_TO_HIGH,
            urlValue: 'price_asc',
        },
        {
            label: 'Price: high to low',
            value: AlgoliaIndice.PRODUCTS_HIGH_TO_LOW,
            urlValue: 'price_desc',
        },
        {
            label: DEFAULT_PLP_ORDER,
            value: AlgoliaIndice.PRODUCTS_BEST_SELLERS,
            urlValue: 'best_seller',
        },
        {
            label: 'New arrivals',
            value: AlgoliaIndice.PRODUCTS_NEW_ARRIVALS,
            urlValue: 'new_arrivals',
        },
        {
            label: 'Top rated',
            value: AlgoliaIndice.PRODUCTS_TOP_RATED,
            urlValue: 'top_rated',
        },
    ],
};

export const SORT_INDEX_PLP = {
    items: [
        {
            label: DEFAULT_PLP_ORDER,
            value: AlgoliaIndice.PRODUCTS_BEST_SELLERS,
            urlValue: 'best-sellers',
        },
        {
            label: DEFAULT_SEARCH_ORDER,
            value: AlgoliaIndice.PRODUCTS,
            urlValue: 'relevance',
        },
        {
            label: 'Price: low to high',
            value: AlgoliaIndice.PRODUCTS_LOW_TO_HIGH,
            urlValue: 'price-low-to-high',
        },
        {
            label: 'Price: high to low',
            value: AlgoliaIndice.PRODUCTS_HIGH_TO_LOW,
            urlValue: 'price-high-to-low',
        },
        {
            label: 'New arrivals',
            value: AlgoliaIndice.PRODUCTS_NEW_ARRIVALS,
            urlValue: 'new-arrival',
        },
        {
            label: 'Top rated',
            value: AlgoliaIndice.PRODUCTS_TOP_RATED,
            urlValue: 'top-rated',
        },
    ],
};

export type DynamicFilterProps = {
    id: string;
    title: string;
    attribute: string;
    limit: number;
    pageId?: string;
    refinements: CurrentRefinementsConnectorParamsItem[];
};

type DynamicFacet = {
    [keyof: string]: Omit<DynamicFilterProps, 'refinements'>;
};

export const configDynamicFacets: DynamicFacet = {
    [FILTERS.SHOP_BY_STORE.ATTRIBUTE]: {
        id: 'inventory',
        title: FILTERS.SHOP_BY_STORE.TITLE,
        attribute: FILTERS.SHOP_BY_STORE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BRAND.ATTRIBUTE]: {
        id: 'brand',
        title: FILTERS.BRAND.TITLE,
        attribute: FILTERS.BRAND.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.RANGE.ATTRIBUTE]: {
        id: 'price',
        title: FILTERS.RANGE.TITLE,
        attribute: FILTERS.RANGE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.RATING.ATTRIBUTE]: {
        id: 'rating',
        title: FILTERS.RATING.TITLE,
        attribute: FILTERS.RATING.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FLAVOR.ATTRIBUTE]: {
        id: 'flavor',
        title: FILTERS.FLAVOR.TITLE,
        attribute: FILTERS.FLAVOR.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BREED_SIZE.ATTRIBUTE]: {
        id: 'breed-size',
        title: FILTERS.BREED_SIZE.TITLE,
        attribute: FILTERS.BREED_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.LIFESTAGE.ATTRIBUTE]: {
        id: 'dog-lifestage',
        title: FILTERS.LIFESTAGE.TITLE,
        attribute: FILTERS.LIFESTAGE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CUSTOM_PET.ATTRIBUTE]: {
        id: 'custom-pet',
        title: FILTERS.CUSTOM_PET.TITLE,
        attribute: FILTERS.CUSTOM_PET.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CUSTOM_CATEGORY.ATTRIBUTE]: {
        id: 'categoryList',
        title: FILTERS.CUSTOM_CATEGORY.TITLE,
        attribute: FILTERS.CUSTOM_CATEGORY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CUSTOM_CATEGORY_LEGACY.ATTRIBUTE]: {
        id: 'customCategory',
        title: FILTERS.CUSTOM_CATEGORY_LEGACY.TITLE,
        attribute: FILTERS.CUSTOM_CATEGORY_LEGACY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CUSTOM_CATEGORY_NAMES.ATTRIBUTE]: {
        id: 'custom-category',
        title: FILTERS.CUSTOM_CATEGORY_NAMES.TITLE,
        attribute: FILTERS.CUSTOM_CATEGORY_NAMES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CUSTOM_WAYS_SHOP.ATTRIBUTE]: {
        id: 'custom-ways-shop',
        title: FILTERS.CUSTOM_WAYS_SHOP.TITLE,
        attribute: FILTERS.CUSTOM_WAYS_SHOP.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PRODUCT_APPLICATION_METHODS.ATTRIBUTE]: {
        id: 'product-application-methods',
        title: FILTERS.PRODUCT_APPLICATION_METHODS.TITLE,
        attribute: FILTERS.PRODUCT_APPLICATION_METHODS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.TANK_SIZE.ATTRIBUTE]: {
        id: 'tank-sizes',
        title: FILTERS.TANK_SIZE.TITLE,
        attribute: FILTERS.TANK_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.AVERAGE_LIFESPANS.ATTRIBUTE]: {
        id: 'average-life-span',
        title: FILTERS.AVERAGE_LIFESPANS.TITLE,
        attribute: FILTERS.AVERAGE_LIFESPANS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PRODUCT_LIFETIME.ATTRIBUTE]: {
        id: 'product-lifetime',
        title: FILTERS.PRODUCT_LIFETIME.TITLE,
        attribute: FILTERS.PRODUCT_LIFETIME.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.GENDER.ATTRIBUTE]: {
        id: 'genders',
        title: FILTERS.GENDER.TITLE,
        attribute: FILTERS.GENDER.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SPORTS_LEAGUE.ATTRIBUTE]: {
        id: 'sports-league',
        title: FILTERS.SPORTS_LEAGUE.TITLE,
        attribute: FILTERS.SPORTS_LEAGUE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.MATERIALS.ATTRIBUTE]: {
        id: 'materials',
        title: FILTERS.MATERIALS.TITLE,
        attribute: FILTERS.MATERIALS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BED_FEATURES.ATTRIBUTE]: {
        id: 'bed-features',
        title: FILTERS.BED_FEATURES.TITLE,
        attribute: FILTERS.BED_FEATURES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BED_SIZE.ATTRIBUTE]: {
        id: 'bed-size',
        title: FILTERS.BED_SIZE.TITLE,
        attribute: FILTERS.BED_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BED_TYPES.ATTRIBUTE]: {
        id: 'bed-type',
        title: FILTERS.BED_TYPES.TITLE,
        attribute: FILTERS.BED_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BIRD_CAGE_DISPLAYS.ATTRIBUTE]: {
        id: 'bird-cage',
        title: FILTERS.BIRD_CAGE_DISPLAYS.TITLE,
        attribute: FILTERS.BIRD_CAGE_DISPLAYS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BIRD_FOOD_TYPES.ATTRIBUTE]: {
        id: 'bird-food',
        title: FILTERS.BIRD_FOOD_TYPES.TITLE,
        attribute: FILTERS.BIRD_FOOD_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_TYPE_BIRDS.ATTRIBUTE]: {
        id: 'pet-type-birds',
        title: FILTERS.PET_TYPE_BIRDS.TITLE,
        attribute: FILTERS.PET_TYPE_BIRDS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_TYPE_BREED.ATTRIBUTE]: {
        id: 'pet-type-breed',
        title: FILTERS.PET_TYPE_BREED.TITLE,
        attribute: FILTERS.PET_TYPE_BREED.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.BULB_TYPE.ATTRIBUTE]: {
        id: 'bulb-type',
        title: FILTERS.BULB_TYPE.TITLE,
        attribute: FILTERS.BULB_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CAT_LIFE_STAGES.ATTRIBUTE]: {
        id: 'cat-lifestage',
        title: FILTERS.CAT_LIFE_STAGES.TITLE,
        attribute: FILTERS.CAT_LIFE_STAGES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CAT_WEIGHT_RANGE.ATTRIBUTE]: {
        id: 'cat-weight-range',
        title: FILTERS.CAT_WEIGHT_RANGE.TITLE,
        attribute: FILTERS.CAT_WEIGHT_RANGE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CAT_WEIGHT_RANGE_DISPLAY.ATTRIBUTE]: {
        id: 'cat-weight-range-display',
        title: FILTERS.CAT_WEIGHT_RANGE_DISPLAY.TITLE,
        attribute: FILTERS.CAT_WEIGHT_RANGE_DISPLAY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.COAT_TYPES.ATTRIBUTE]: {
        id: 'coat-types',
        title: FILTERS.COAT_TYPES.TITLE,
        attribute: FILTERS.COAT_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.COLLAR_LEASH_SIZE_DISPLAY.ATTRIBUTE]: {
        id: 'collar-leash-size',
        title: FILTERS.COLLAR_LEASH_SIZE_DISPLAY.TITLE,
        attribute: FILTERS.COLLAR_LEASH_SIZE_DISPLAY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.COLLECTION.ATTRIBUTE]: {
        id: 'collections',
        title: FILTERS.COLLECTION.TITLE,
        attribute: FILTERS.COLLECTION.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.COLOR_VALUE.ATTRIBUTE]: {
        id: 'color-value',
        title: FILTERS.COLOR_VALUE.TITLE,
        attribute: FILTERS.COLOR_VALUE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.COLOR_SWATCH_DISPLAY.ATTRIBUTE]: {
        id: 'color-swatch-display',
        title: FILTERS.COLOR_SWATCH_DISPLAY.TITLE,
        attribute: FILTERS.COLOR_SWATCH_DISPLAY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.COLOR_FAMILIES.ATTRIBUTE]: {
        id: 'color-families',
        title: FILTERS.COLOR_FAMILIES.TITLE,
        attribute: FILTERS.COLOR_FAMILIES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CLIMATE_CONTROL_DEVICES.ATTRIBUTE]: {
        id: 'climate-control',
        title: FILTERS.CLIMATE_CONTROL_DEVICES.TITLE,
        attribute: FILTERS.CLIMATE_CONTROL_DEVICES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.COSTUME_TYPES.ATTRIBUTE]: {
        id: 'costume-types',
        title: FILTERS.COSTUME_TYPES.TITLE,
        attribute: FILTERS.COSTUME_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CRATE_CARRIER_TYPE.ATTRIBUTE]: {
        id: 'crate-carrier-type',
        title: FILTERS.CRATE_CARRIER_TYPE.TITLE,
        attribute: FILTERS.CRATE_CARRIER_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CRATE_CARRIER_SIZE.ATTRIBUTE]: {
        id: 'crate-size',
        title: FILTERS.CRATE_CARRIER_SIZE.TITLE,
        attribute: FILTERS.CRATE_CARRIER_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.CRATE_MATS_PADS_DISPLAY.ATTRIBUTE]: {
        id: 'crate-mats-pads',
        title: FILTERS.CRATE_MATS_PADS_DISPLAY.TITLE,
        attribute: FILTERS.CRATE_MATS_PADS_DISPLAY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.DIMENSION_RANGES.ATTRIBUTE]: {
        id: 'dimension-ranges',
        title: FILTERS.DIMENSION_RANGES.TITLE,
        attribute: FILTERS.DIMENSION_RANGES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.DISCONTINUED_OVERRIDE.ATTRIBUTE]: {
        id: 'discounted-override',
        title: FILTERS.DISCONTINUED_OVERRIDE.TITLE,
        attribute: FILTERS.DISCONTINUED_OVERRIDE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.DOG_ACCESSORIES.ATTRIBUTE]: {
        id: 'dog-accessories',
        title: FILTERS.DOG_ACCESSORIES.TITLE,
        attribute: FILTERS.DOG_ACCESSORIES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.DOG_WEIGHT_RANGE.ATTRIBUTE]: {
        id: 'dog-weiht-range',
        title: FILTERS.DOG_WEIGHT_RANGE.TITLE,
        attribute: FILTERS.DOG_WEIGHT_RANGE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.DOOR_GATE_TYPE.ATTRIBUTE]: {
        id: 'door-gate-types',
        title: FILTERS.DOOR_GATE_TYPE.TITLE,
        attribute: FILTERS.DOOR_GATE_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.ENVIRONMENT.ATTRIBUTE]: {
        id: 'environments',
        title: FILTERS.ENVIRONMENT.TITLE,
        attribute: FILTERS.ENVIRONMENT.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.EXPERIENCE_LEVEL.ATTRIBUTE]: {
        id: 'experience-levels',
        title: FILTERS.EXPERIENCE_LEVEL.TITLE,
        attribute: FILTERS.EXPERIENCE_LEVEL.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FENCE_SYSTEM_TYPE.ATTRIBUTE]: {
        id: 'fence-systems',
        title: FILTERS.FENCE_SYSTEM_TYPE.TITLE,
        attribute: FILTERS.FENCE_SYSTEM_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FILTER_TYPE.ATTRIBUTE]: {
        id: 'filter-types',
        title: FILTERS.FILTER_TYPE.TITLE,
        attribute: FILTERS.FILTER_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FISH_FEEDER_TYPE.ATTRIBUTE]: {
        id: 'fish-feeder-types',
        title: FILTERS.FISH_FEEDER_TYPE.TITLE,
        attribute: FILTERS.FISH_FEEDER_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FISH_FOOD_TYPES.ATTRIBUTE]: {
        id: 'fish-food-types',
        title: FILTERS.FISH_FOOD_TYPES.TITLE,
        attribute: FILTERS.FISH_FOOD_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_TYPE_FISH.ATTRIBUTE]: {
        id: 'pet-type-fish',
        title: FILTERS.PET_TYPE_FISH.TITLE,
        attribute: FILTERS.PET_TYPE_FISH.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FLAVOR_VALUE.ATTRIBUTE]: {
        id: 'flavor-value',
        title: FILTERS.FLAVOR_VALUE.TITLE,
        attribute: FILTERS.FLAVOR_VALUE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SIZE_FLUID.ATTRIBUTE]: {
        id: 'size-fluid',
        title: FILTERS.SIZE_FLUID.TITLE,
        attribute: FILTERS.SIZE_FLUID.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FOOD_CATEGORY.ATTRIBUTE]: {
        id: 'food-category',
        title: FILTERS.FOOD_CATEGORY.TITLE,
        attribute: FILTERS.FOOD_CATEGORY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FOOD_FORMS.ATTRIBUTE]: {
        id: 'food-forms',
        title: FILTERS.FOOD_FORMS.TITLE,
        attribute: FILTERS.FOOD_FORMS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FOOD_OUNCES_SIZE.ATTRIBUTE]: {
        id: 'food-ounces',
        title: FILTERS.FOOD_OUNCES_SIZE.TITLE,
        attribute: FILTERS.FOOD_OUNCES_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FOOD_POUNDS_SIZE.ATTRIBUTE]: {
        id: 'food-pounds',
        title: FILTERS.FOOD_POUNDS_SIZE.TITLE,
        attribute: FILTERS.FOOD_POUNDS_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FOOD_STORAGE_CAPACITY.ATTRIBUTE]: {
        id: 'food-storage-capacity',
        title: FILTERS.FOOD_STORAGE_CAPACITY.TITLE,
        attribute: FILTERS.FOOD_STORAGE_CAPACITY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FOOD_TEXTURE.ATTRIBUTE]: {
        id: 'food-texture',
        title: FILTERS.FOOD_TEXTURE.TITLE,
        attribute: FILTERS.FOOD_TEXTURE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FURNITURE_HEIGHT.ATTRIBUTE]: {
        id: 'furniture-height',
        title: FILTERS.FURNITURE_HEIGHT.TITLE,
        attribute: FILTERS.FURNITURE_HEIGHT.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.GATE_SIZE.ATTRIBUTE]: {
        id: 'gate-sizes',
        title: FILTERS.GATE_SIZE.TITLE,
        attribute: FILTERS.GATE_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.GROOMING_TYPES.ATTRIBUTE]: {
        id: 'grooming-types',
        title: FILTERS.GROOMING_TYPES.TITLE,
        attribute: FILTERS.GROOMING_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.HABITAT_ACCESSORIES.ATTRIBUTE]: {
        id: 'habitat-accessories',
        title: FILTERS.HABITAT_ACCESSORIES.TITLE,
        attribute: FILTERS.HABITAT_ACCESSORIES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.HABITAT_DECOR_TYPE.ATTRIBUTE]: {
        id: 'decor-type',
        title: FILTERS.HABITAT_DECOR_TYPE.TITLE,
        attribute: FILTERS.HABITAT_DECOR_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.HEALTH.ATTRIBUTE]: {
        id: 'health-considerations',
        title: FILTERS.HEALTH.TITLE,
        attribute: FILTERS.HEALTH.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE]: {
        id: 'subscription-enabled',
        title: FILTERS.SUBSCRIPTION_ENABLED.TITLE,
        attribute: FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.LEASH_TYPE.ATTRIBUTE]: {
        id: 'leash-types',
        title: FILTERS.LEASH_TYPE.TITLE,
        attribute: FILTERS.LEASH_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.LITTER_BOX_TYPE.ATTRIBUTE]: {
        id: 'litter-box-type',
        title: FILTERS.LITTER_BOX_TYPE.TITLE,
        attribute: FILTERS.LITTER_BOX_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.LITTER_TYPE.ATTRIBUTE]: {
        id: 'litter-type',
        title: FILTERS.LITTER_TYPE.TITLE,
        attribute: FILTERS.LITTER_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.LITTER_FEATURES.ATTRIBUTE]: {
        id: 'litter-features',
        title: FILTERS.LITTER_FEATURES.TITLE,
        attribute: FILTERS.LITTER_FEATURES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.LITTER_MATERIALS.ATTRIBUTE]: {
        id: 'litter-materials',
        title: FILTERS.LITTER_MATERIALS.TITLE,
        attribute: FILTERS.LITTER_MATERIALS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.LIVE_PET.ATTRIBUTE]: {
        id: 'live-pet',
        title: FILTERS.LIVE_PET.TITLE,
        attribute: FILTERS.LIVE_PET.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.MATERIALS.ATTRIBUTE]: {
        id: 'materials',
        title: FILTERS.MATERIALS.TITLE,
        attribute: FILTERS.MATERIALS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.MATURITY_SIZE.ATTRIBUTE]: {
        id: 'maturity-size',
        title: FILTERS.MATURITY_SIZE.TITLE,
        attribute: FILTERS.MATURITY_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.NUTRITIONAL_OPTION.ATTRIBUTE]: {
        id: 'nutritional-options',
        title: FILTERS.NUTRITIONAL_OPTION.TITLE,
        attribute: FILTERS.NUTRITIONAL_OPTION.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PACKAGE_TYPE.ATTRIBUTE]: {
        id: 'package-type',
        title: FILTERS.PACKAGE_TYPE.TITLE,
        attribute: FILTERS.PACKAGE_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_TYPE_PETS.ATTRIBUTE]: {
        id: 'pet-type-pets',
        title: FILTERS.PET_TYPE_PETS.TITLE,
        attribute: FILTERS.PET_TYPE_PETS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_TYPE_AMPHIBIANS.ATTRIBUTE]: {
        id: 'pet-type-amphibians',
        title: FILTERS.PET_TYPE_AMPHIBIANS.TITLE,
        attribute: FILTERS.PET_TYPE_AMPHIBIANS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_SIZE_DISPLAY.ATTRIBUTE]: {
        id: 'pet-size-displays',
        title: FILTERS.PET_SIZE_DISPLAY.TITLE,
        attribute: FILTERS.PET_SIZE_DISPLAY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SIZE_PET_SIZE.ATTRIBUTE]: {
        id: 'size-pet-size',
        title: FILTERS.SIZE_PET_SIZE.TITLE,
        attribute: FILTERS.SIZE_PET_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PHARMACY_SKU.ATTRIBUTE]: {
        id: 'pharmacy-sku',
        title: FILTERS.PHARMACY_SKU.TITLE,
        attribute: FILTERS.PHARMACY_SKU.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PHARMACY_TYPE.ATTRIBUTE]: {
        id: 'pharmacy-type',
        title: FILTERS.PHARMACY_TYPE.TITLE,
        attribute: FILTERS.PHARMACY_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PHARMACY_COLD_STORAGE.ATTRIBUTE]: {
        id: 'pharmacy-cold-storage',
        title: FILTERS.PHARMACY_COLD_STORAGE.TITLE,
        attribute: FILTERS.PHARMACY_COLD_STORAGE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PHARMACY_OUT_OF_STOCK.ATTRIBUTE]: {
        id: 'pharmacy-out-of-stock',
        title: FILTERS.PHARMACY_OUT_OF_STOCK.TITLE,
        attribute: FILTERS.PHARMACY_OUT_OF_STOCK.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.QUANTITY.ATTRIBUTE]: {
        id: 'quantity',
        title: FILTERS.QUANTITY.TITLE,
        attribute: FILTERS.QUANTITY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.QUANTITY_DISPLAY.ATTRIBUTE]: {
        id: 'quantity-display',
        title: FILTERS.QUANTITY_DISPLAY.TITLE,
        attribute: FILTERS.QUANTITY_DISPLAY.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.FENCE_RANGES.ATTRIBUTE]: {
        id: 'fence-ranges',
        title: FILTERS.FENCE_RANGES.TITLE,
        attribute: FILTERS.FENCE_RANGES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.REPTILE_FOOD_TYPE.ATTRIBUTE]: {
        id: 'reptile-food-type',
        title: FILTERS.REPTILE_FOOD_TYPE.TITLE,
        attribute: FILTERS.REPTILE_FOOD_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_TYPE_REPTILES.ATTRIBUTE]: {
        id: 'pet-type-reptile',
        title: FILTERS.PET_TYPE_REPTILES.TITLE,
        attribute: FILTERS.PET_TYPE_REPTILES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PHARMACY_STRENGTH.ATTRIBUTE]: {
        id: 'pharmacy-strength',
        title: FILTERS.PHARMACY_STRENGTH.TITLE,
        attribute: FILTERS.PHARMACY_STRENGTH.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SCENT.ATTRIBUTE]: {
        id: 'scent',
        title: FILTERS.SCENT.TITLE,
        attribute: FILTERS.SCENT.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE]: {
        id: 'scheduled-delivery',
        title: FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.TITLE,
        attribute: FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SERIES.ATTRIBUTE]: {
        id: 'scent',
        title: FILTERS.SERIES.TITLE,
        attribute: FILTERS.SERIES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SCENT.ATTRIBUTE]: {
        id: 'series',
        title: FILTERS.SCENT.TITLE,
        attribute: FILTERS.SCENT.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SHAPES.ATTRIBUTE]: {
        id: 'shapes',
        title: FILTERS.SHAPES.TITLE,
        attribute: FILTERS.SHAPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.PET_TYPE_SMALL_PETS.ATTRIBUTE]: {
        id: 'pet-type-small-pets',
        title: FILTERS.PET_TYPE_SMALL_PETS.TITLE,
        attribute: FILTERS.PET_TYPE_SMALL_PETS.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SMALL_PET_ACCESSORIES.ATTRIBUTE]: {
        id: 'small-pet-accessories',
        title: FILTERS.SMALL_PET_ACCESSORIES.TITLE,
        attribute: FILTERS.SMALL_PET_ACCESSORIES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SIZE_SOLID_SIZE.ATTRIBUTE]: {
        id: 'size-solid-size',
        title: FILTERS.SIZE_SOLID_SIZE.TITLE,
        attribute: FILTERS.SIZE_SOLID_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.STAIN_TYPES.ATTRIBUTE]: {
        id: 'stain-types',
        title: FILTERS.STAIN_TYPES.TITLE,
        attribute: FILTERS.STAIN_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.STAND_TYPE.ATTRIBUTE]: {
        id: 'stand-type',
        title: FILTERS.STAND_TYPE.TITLE,
        attribute: FILTERS.STAND_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SHIP_TO_HOME_CASE.ATTRIBUTE]: {
        id: 'ship-to-home-cas',
        title: FILTERS.SHIP_TO_HOME_CASE.TITLE,
        attribute: FILTERS.SHIP_TO_HOME_CASE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.ITEMS_PER_PACK.ATTRIBUTE]: {
        id: 'items-per-package',
        title: FILTERS.ITEMS_PER_PACK.TITLE,
        attribute: FILTERS.ITEMS_PER_PACK.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.DESIGN_THEME.ATTRIBUTE]: {
        id: 'design-theme',
        title: FILTERS.DESIGN_THEME.TITLE,
        attribute: FILTERS.DESIGN_THEME.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.TOY_FEATURES.ATTRIBUTE]: {
        id: 'toy-features',
        title: FILTERS.TOY_FEATURES.TITLE,
        attribute: FILTERS.TOY_FEATURES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.TREAT_TYPE.ATTRIBUTE]: {
        id: 'treat-type',
        title: FILTERS.TREAT_TYPE.TITLE,
        attribute: FILTERS.TREAT_TYPE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.VET_AUTHORIZED_DIET.ATTRIBUTE]: {
        id: 'vet-authorized',
        title: FILTERS.VET_AUTHORIZED_DIET.TITLE,
        attribute: FILTERS.VET_AUTHORIZED_DIET.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.WATER_TREATMENT.ATTRIBUTE]: {
        id: 'water-treatment',
        title: FILTERS.WATER_TREATMENT.TITLE,
        attribute: FILTERS.WATER_TREATMENT.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.WATER_TYPES.ATTRIBUTE]: {
        id: 'water-types',
        title: FILTERS.WATER_TYPES.TITLE,
        attribute: FILTERS.WATER_TYPES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.WATTAGE.ATTRIBUTE]: {
        id: 'wattage',
        title: FILTERS.WATTAGE.TITLE,
        attribute: FILTERS.WATTAGE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.WATTAGE_RANGES.ATTRIBUTE]: {
        id: 'wattage-range',
        title: FILTERS.WATTAGE_RANGES.TITLE,
        attribute: FILTERS.WATTAGE_RANGES.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.THREE_DIMENSION_SIZE.ATTRIBUTE]: {
        id: 'size-3d',
        title: FILTERS.THREE_DIMENSION_SIZE.TITLE,
        attribute: FILTERS.THREE_DIMENSION_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.TWO_DIMENSION_SIZE.ATTRIBUTE]: {
        id: 'size-2d',
        title: FILTERS.TWO_DIMENSION_SIZE.TITLE,
        attribute: FILTERS.TWO_DIMENSION_SIZE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.REVIEW_COUNT.ATTRIBUTE]: {
        id: 'review-count',
        title: FILTERS.REVIEW_COUNT.TITLE,
        attribute: FILTERS.REVIEW_COUNT.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.INTENDED_PRODUCT_USE.ATTRIBUTE]: {
        id: 'intended-use',
        title: FILTERS.INTENDED_PRODUCT_USE.TITLE,
        attribute: FILTERS.INTENDED_PRODUCT_USE.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
    [FILTERS.SPORTS_TEAM.ATTRIBUTE]: {
        id: 'sports-team',
        title: FILTERS.SPORTS_TEAM.TITLE,
        attribute: FILTERS.SPORTS_TEAM.ATTRIBUTE,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    },
};

export const analyticsSortProducts: Record<string, string> = {
    [AlgoliaIndice.PRODUCTS]: 'relevance',
    [AlgoliaIndice.PRODUCTS_BEST_SELLERS]: 'best_sellers',
    [AlgoliaIndice.PRODUCTS_LOW_TO_HIGH]: 'price-low-to-high',
    [AlgoliaIndice.PRODUCTS_HIGH_TO_LOW]: 'price-high-to-low',
    [AlgoliaIndice.PRODUCTS_NEW_ARRIVALS]: 'new-arrivals',
    [AlgoliaIndice.PRODUCTS_TOP_RATED]: 'top-rated',
};

export const fixedFilters = [FILTERS.BRAND.ATTRIBUTE, FILTERS.RANGE.ATTRIBUTE, FILTERS.RATING.ATTRIBUTE];

export const getDynamicFacetInfo = (attribute: string) =>
    configDynamicFacets[attribute] ?? {
        id: attribute,
        title: attribute,
        attribute,
        limit: FILTERS.REFINEMENT_LIST.SHOW_MORE_LIMIT,
    };

export const ORDERED_SEO_FRIENDLY_FACET_FILTERS = {
    [FILTERS.BRAND.ATTRIBUTE]: 'brand',
    [FILTERS.FLAVOR.ATTRIBUTE]: 'flavor',
    [FILTERS.CUSTOM_CATEGORY.ATTRIBUTE]: 'category',
    [FILTERS.PET_TYPE_SMALL_PETS.ATTRIBUTE]: 'smallPetType',
    [FILTERS.CUSTOM_PET.ATTRIBUTE]: 'pet',
    [FILTERS.COLOR_FAMILIES.ATTRIBUTE]: 'colorFamily',
    [FILTERS.PET_TYPE_REPTILES.ATTRIBUTE]: 'reptileType',
    [FILTERS.PET_TYPE_FISH.ATTRIBUTE]: 'petFishType',
    [FILTERS.PET_TYPE_BIRDS.ATTRIBUTE]: 'birdType',
    [FILTERS.PET_TYPE_BREED.ATTRIBUTE]: 'breedType',
    [FILTERS.INTENDED_PRODUCT_USE.ATTRIBUTE]: 'intendedUse',
    [FILTERS.MATERIALS.ATTRIBUTE]: 'material',
    [FILTERS.NUTRITIONAL_OPTION.ATTRIBUTE]: 'nutritionalOption',
    [FILTERS.HEALTH.ATTRIBUTE]: 'health',
    [FILTERS.BIRD_FOOD_TYPES.ATTRIBUTE]: 'birdFood',
    [FILTERS.FISH_FOOD_TYPES.ATTRIBUTE]: 'fishFoodType',
    [FILTERS.REPTILE_FOOD_TYPE.ATTRIBUTE]: 'reptileFoodType',
    [FILTERS.LITTER_FEATURES.ATTRIBUTE]: 'litterFeature',
    [FILTERS.TOY_FEATURES.ATTRIBUTE]: 'toyFeature',
    [FILTERS.SERIES.ATTRIBUTE]: 'serie',
    [FILTERS.FOOD_FORMS.ATTRIBUTE]: 'foodForm',
    [FILTERS.BREED_SIZE.ATTRIBUTE]: 'breedSize',
    [FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE]: 'subscriptionEnabled',
    [FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE]: 'scheduledDelivery',
};

const FLAVOR_LEGACY_FACET_ID = 'flavor.displays';

/**
 * Merchandising facet order comes from Algolia `renderingContent.facetOrdering.facets.order`.
 * Merchandising may list both `customCategory` and `customCategoryList` (Facet Display / Configure Facets).
 * The PLP drawer uses a single Category control backed by {@link FILTERS.CUSTOM_CATEGORY}; always map legacy
 * `customCategory` entries to that attribute and dedupe so we do not render two category filters.
 */
export function normalizeDynamicFacetOrder(order: readonly string[]): string[] {
    const canonical = FILTERS.CUSTOM_CATEGORY.ATTRIBUTE;
    const legacy = FILTERS.CUSTOM_CATEGORY_LEGACY.ATTRIBUTE;
    const flavorCanonical = FILTERS.FLAVOR.ATTRIBUTE;
    // [WARNING]: Modifying this logic will break the PLP drawer.
    const mapped = order.map(f => {
        if (f === legacy) {
            return canonical;
        }
        if (f === FLAVOR_LEGACY_FACET_ID) {
            return flavorCanonical;
        }
        return f;
    });
    return [...new Set(mapped)];
}

export const getAlgoliaPersistentProductFilters = (onlineAt = Date.now()) => {
    return `isSKUAvailable: true AND onlineFrom < ${onlineAt} AND onlineTo > ${onlineAt}`;
};
